import { Fragment, useState, useMemo } from 'react';
import {
    Box,
    Card,
    Typography,
    CircularProgress,
    Alert,
    FormControlLabel,
    Checkbox,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    useDataProvider,
    useTranslate,
    useNotify,
    downloadCSV,
    Title,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useQuery } from '@tanstack/react-query';
import { LineChart } from '@mui/x-charts/LineChart';
import { LoginStats, LoginStatsHistoryItem } from './statistics/types';
import { numberFormatter } from './statistics/common';
import { LoginFailureList } from './statistics/LoginStats';
import StaleDataAlert from './statistics/StaleDataAlert';

// Date -> yyyy-mm-dd (local) for <input type="date">.
const toInputDate = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const helsinkiHour = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Helsinki',
    hour: '2-digit',
    hourCycle: 'h23',
});

// Match the history API's Helsinki calendar days, including 23/25-hour DST days.
const dayBounds = (date: string, rangeFrom: string, rangeTo: string) => {
    const midnightUtc = Date.parse(`${date}T00:00:00Z`);
    const midnight = (utc: number) =>
        utc - Number(helsinkiHour.format(utc)) * 60 * 60 * 1000;
    return {
        from: new Date(
            Math.max(midnight(midnightUtc), Date.parse(rangeFrom))
        ).toISOString(),
        to: new Date(
            Math.min(
                midnight(midnightUtc + 24 * 60 * 60 * 1000) - 1,
                Date.parse(rangeTo)
            )
        ).toISOString(),
    };
};

const DailyLoginFailures = ({
    date,
    from,
    to,
}: {
    date: string;
    from: string;
    to: string;
}) => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const bounds = dayBounds(date, from, to);
    const params = `filter=${JSON.stringify(bounds)}`;
    const { data, isPending, error } = useQuery<LoginStats>({
        queryKey: ['loginfailures', bounds],
        queryFn: async () => {
            const response: { data: LoginStats } = await dataProvider.getAny(
                '/statistics/loginstats',
                params
            );
            return response.data;
        },
        staleTime: 300_000,
    });

    return (
        <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate('statistics.loginstats.failure_details')} – {date}
            </Typography>
            {isPending ? (
                <CircularProgress size={24} />
            ) : error ? (
                <Alert severity="error">{error.message}</Alert>
            ) : data ? (
                <>
                    <StaleDataAlert
                        stale={data.stale}
                        cachedAt={data.cached_at}
                    />
                    <LoginFailureList data={data} />
                </>
            ) : null}
        </Box>
    );
};

const LoginStatsPage = () => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const { canAccess } = useCanAccess({
        action: 'show',
        resource: 'loginstats',
    });

    const now = new Date();
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const [from, setFrom] = useState<string>(toInputDate(monthAgo));
    const [to, setTo] = useState<string>(toInputDate(now));
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
    const toggleDate = (date: string) => {
        setExpandedDates(previous => {
            const next = new Set(previous);
            if (next.has(date)) next.delete(date);
            else next.add(date);
            return next;
        });
    };
    const [visible, setVisible] = useState({
        success: true,
        failed: true,
        mpass_success: true,
        mpass_failed: true,
    });

    const params = `filter=${JSON.stringify({ from, to, group_by: 'day' })}`;
    const { data, isPending, error } = useQuery<LoginStatsHistoryItem[]>({
        queryKey: ['loginstatshistory', params],
        queryFn: () =>
            (dataProvider as any)
                .getAny('/statistics/loginstats/history', params)
                .then((res: any) => res.data ?? res),
        enabled: canAccess === true && !!from && !!to,
        staleTime: Infinity,
    });

    const rows = data || [];
    const dates = rows.map(r => r.date);

    const allSeries = useMemo(
        () => [
            {
                key: 'success' as const,
                label: translate('statistics.loginstats.success'),
                color: '#2e7d32',
                data: rows.map(r => r.success),
            },
            {
                key: 'failed' as const,
                label: translate('statistics.loginstats.failed'),
                color: '#c62828',
                data: rows.map(r => r.failed),
            },
            {
                key: 'mpass_success' as const,
                label: `MPASS ${translate('statistics.loginstats.success')}`,
                color: '#81c784',
                data: rows.map(r => r.mpass_success),
            },
            {
                key: 'mpass_failed' as const,
                label: `MPASS ${translate('statistics.loginstats.failed')}`,
                color: '#e57373',
                data: rows.map(r => r.mpass_failed),
            },
        ],
        [rows, translate]
    );

    const series = allSeries.filter(s => visible[s.key]);

    const handleExport = () => {
        const dateHeader = translate('statistics.activeusershistory.date');
        const csvRows = rows.map(r => ({
            [dateHeader]: r.date,
            [translate('statistics.loginstats.success')]: r.success,
            [translate('statistics.loginstats.failed')]: r.failed,
            [`MPASS ${translate('statistics.loginstats.success')}`]:
                r.mpass_success,
            [`MPASS ${translate('statistics.loginstats.failed')}`]:
                r.mpass_failed,
        }));
        jsonExport(csvRows, { rowDelimiter: ';' }, (err, csv) => {
            if (err) {
                notify(translate('notifications.EXPORT_ERROR'), {
                    type: 'error',
                });
                return;
            }
            // UTF-8 BOM so Excel renders Scandinavian letters correctly.
            downloadCSV(`\ufeff${csv}`, `login-stats-${from}_${to}`);
        });
    };

    if (!canAccess) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {translate('statistics.noaccess')}
            </Alert>
        );
    }

    const hasData = rows.length > 0;

    return (
        <Box sx={{ p: 2 }}>
            <Title title={translate('statistics.loginstats.title')} />
            <Typography
                variant="h6"
                gutterBottom
                color="text.secondary"
                sx={{ fontWeight: 'normal' }}
            >
                {translate('statistics.loginstats.title')}
            </Typography>
            <Card>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <TextField
                        type="date"
                        size="small"
                        label={translate('statistics.activeusershistory.from')}
                        InputLabelProps={{ shrink: true }}
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label={translate('statistics.activeusershistory.to')}
                        InputLabelProps={{ shrink: true }}
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        sx={{ width: 160 }}
                    />
                    {allSeries.map(s => (
                        <FormControlLabel
                            key={s.key}
                            control={
                                <Checkbox
                                    checked={visible[s.key]}
                                    onChange={e =>
                                        setVisible(v => ({
                                            ...v,
                                            [s.key]: e.target.checked,
                                        }))
                                    }
                                />
                            }
                            label={s.label}
                        />
                    ))}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={!hasData}
                    >
                        {translate('statistics.activeusershistory.export')}
                    </Button>
                </Box>

                <Box sx={{ p: 2 }}>
                    {isPending ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                p: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">
                            {(error as Error).message}
                        </Alert>
                    ) : !hasData ? (
                        <Alert severity="warning">
                            {translate('statistics.nodata')}
                        </Alert>
                    ) : (
                        <>
                            <LineChart
                                height={320}
                                xAxis={[{ data: dates, scaleType: 'point' }]}
                                series={series.map(s => ({
                                    label: s.label,
                                    color: s.color,
                                    data: s.data,
                                    showMark: false,
                                }))}
                            />
                            <TableContainer component={Paper} sx={{ mt: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {translate(
                                                    'statistics.activeusershistory.date'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {translate(
                                                    'statistics.loginstats.success'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {translate(
                                                    'statistics.loginstats.failed'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                MPASS{' '}
                                                {translate(
                                                    'statistics.loginstats.success'
                                                ).toLowerCase()}
                                            </TableCell>
                                            <TableCell align="right">
                                                MPASS{' '}
                                                {translate(
                                                    'statistics.loginstats.failed'
                                                ).toLowerCase()}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map(r => (
                                            <Fragment key={r.date}>
                                                <TableRow>
                                                    <TableCell>
                                                        {r.date}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {numberFormatter.format(
                                                            r.success
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {r.failed > 0 ? (
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                aria-expanded={expandedDates.has(
                                                                    r.date
                                                                )}
                                                                aria-controls={`login-failures-${r.date}`}
                                                                aria-label={`${translate('statistics.loginstats.failure_details')} ${r.date}`}
                                                                endIcon={
                                                                    expandedDates.has(
                                                                        r.date
                                                                    ) ? (
                                                                        <ExpandLessIcon />
                                                                    ) : (
                                                                        <ExpandMoreIcon />
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    toggleDate(
                                                                        r.date
                                                                    )
                                                                }
                                                            >
                                                                {numberFormatter.format(
                                                                    r.failed
                                                                )}
                                                            </Button>
                                                        ) : (
                                                            numberFormatter.format(
                                                                r.failed
                                                            )
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {numberFormatter.format(
                                                            r.mpass_success
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {numberFormatter.format(
                                                            r.mpass_failed
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                {r.failed > 0 &&
                                                    expandedDates.has(
                                                        r.date
                                                    ) && (
                                                        <TableRow
                                                            id={`login-failures-${r.date}`}
                                                        >
                                                            <TableCell
                                                                colSpan={5}
                                                                sx={{
                                                                    bgcolor:
                                                                        'action.hover',
                                                                    p: 0,
                                                                }}
                                                            >
                                                                <DailyLoginFailures
                                                                    date={
                                                                        r.date
                                                                    }
                                                                    from={from}
                                                                    to={to}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                            </Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default LoginStatsPage;
